import { isHomeTileInternalKey, type HomeTileInternalKey } from '@gftg/shared';
import type { PublicEvent } from './queries';

export type Tile = PublicEvent['tiles'][number];

/**
 * Organizers own which tiles exist, their order and their wording (HOME-04). The app owns
 * where a destination key leads and how the tile is presented — the design splits the same
 * organizer list into a personal grid ("Yours") and a conference list, so the split is a
 * property of the destination, not of the row.
 */
export const ROUTES: Partial<Record<HomeTileInternalKey, string>> = {
  agenda: '/program',
  speakers: '/speakers',
  venue: '/venue',
  schedule: '/schedule',
  matches: '/matches',
  directory: '/directory',
};

/**
 * Destinations an organizer can put on the home screen that this build cannot open yet. They are
 * shown as locked rather than hidden: the tile is real, the door is not open yet, and quietly
 * dropping it would look like a bug to the organizer who put it there.
 *
 * The information pages (VEN-02) have an endpoint and no screen, and the client has not written
 * their text, so an `info` tile says so instead of leading nowhere.
 */
export const LOCKED_NOTE: Partial<Record<HomeTileInternalKey, string>> = {
  info: 'The information pages are not published yet',
};

const PERSONAL_KEYS: HomeTileInternalKey[] = ['schedule', 'matches', 'directory'];

export function tileKey(tile: Tile): HomeTileInternalKey | null {
  return tile.internalKey && isHomeTileInternalKey(tile.internalKey) ? tile.internalKey : null;
}

export interface GroupedTiles {
  /** The 2×2 grid: what belongs to this attendee. */
  personal: Tile[];
  /** The list: the conference itself, then the links that leave the app. */
  conference: Tile[];
  /** Surfaced as its own row above the grid (HOME-03). */
  announcements: Tile | null;
}

export function groupTiles(tiles: Tile[]): GroupedTiles {
  const personal: Tile[] = [];
  const conference: Tile[] = [];
  let announcements: Tile | null = null;

  for (const tile of tiles) {
    const key = tileKey(tile);

    if (key === 'profile') {
      // The avatar in the header is the way to the profile, so a profile tile is dropped rather
      // than shown twice — even if an organizer adds one back.
      continue;
    }

    if (key === 'announcements') {
      announcements = tile;
    } else if (key && PERSONAL_KEYS.includes(key)) {
      personal.push(tile);
    } else {
      conference.push(tile);
    }
  }

  return { personal, conference, announcements };
}

/**
 * What a tile says under its label when the organizer left the subtitle empty. Keeping it live
 * means the home screen never advertises "8 speakers" a week after a ninth was added.
 */
export function derivedSubtitle(tile: Tile, event: PublicEvent): string | null {
  if (tile.subtitle) {
    return tile.subtitle;
  }

  switch (tileKey(tile)) {
    case 'agenda':
      return event.days.length > 0 ? `All ${event.days.length} days, by track` : null;
    case 'speakers':
      return event.stats.speakers > 0 ? `${event.stats.speakers} on the program` : null;
    case 'venue':
      return event.venueName;
    default:
      return null;
  }
}
